import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { rbacApi, RoleMappingResponse } from '../../services/api/rbac';
import { UserRole, ROLE_LABELS } from '../../contexts/RoleContext';
import { PolicyManagement } from './PolicyManagement';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';

type AdminTab = 'roles' | 'policies';

const ALL_ROLES: UserRole[] = ['viewer', 'operator', 'admin', 'system_admin'];
const BREAK_GLASS_GROUP = 'TransferHub-BreakGlass';
const OKTA_SYNCED_DESCRIPTION = 'Synced from Okta';

const ROLE_COLORS: Record<UserRole, { dot: string; bg: string; text: string; border: string }> = {
  viewer: {
    dot: 'bg-neutral-400',
    bg: 'bg-neutral-50 dark:bg-neutral-800/50',
    text: 'text-neutral-700 dark:text-neutral-300',
    border: 'border-neutral-200 dark:border-neutral-700',
  },
  operator: {
    dot: 'bg-primary-500',
    bg: 'bg-primary-50 dark:bg-primary-900/20',
    text: 'text-primary-700 dark:text-primary-300',
    border: 'border-primary-200 dark:border-primary-800',
  },
  admin: {
    dot: 'bg-warning-500',
    bg: 'bg-warning-50 dark:bg-warning-900/20',
    text: 'text-warning-700 dark:text-warning-300',
    border: 'border-warning-200 dark:border-warning-800',
  },
  system_admin: {
    dot: 'bg-danger-500',
    bg: 'bg-danger-50 dark:bg-danger-900/20',
    text: 'text-danger-700 dark:text-danger-300',
    border: 'border-danger-200 dark:border-danger-800',
  },
};

const PERMISSION_MATRIX: { category: string; permissions: { label: string; roles: UserRole[] }[] }[] = [
  {
    category: 'View & Monitor',
    permissions: [
      { label: 'View partners, connections, tasks', roles: ['viewer', 'operator', 'admin', 'system_admin'] },
      { label: 'View file events & audit logs', roles: ['viewer', 'operator', 'admin', 'system_admin'] },
    ],
  },
  {
    category: 'Operations',
    permissions: [
      { label: 'Enable / disable tasks', roles: ['operator', 'admin', 'system_admin'] },
      { label: 'Test connections', roles: ['operator', 'admin', 'system_admin'] },
      { label: 'Trigger manual file transfers', roles: ['operator', 'admin', 'system_admin'] },
    ],
  },
  {
    category: 'Management',
    permissions: [
      { label: 'Create & edit partners', roles: ['admin', 'system_admin'] },
      { label: 'Create, edit & delete connections', roles: ['admin', 'system_admin'] },
      { label: 'Create, edit & delete tasks', roles: ['admin', 'system_admin'] },
    ],
  },
  {
    category: 'System',
    permissions: [
      { label: 'Delete partners (cascade)', roles: ['system_admin'] },
      { label: 'Manage role mappings', roles: ['system_admin'] },
    ],
  },
];

type SortField = 'groupName' | 'role';
type SortDir = 'asc' | 'desc';

interface MappingRow {
  groupName: string;
  role: UserRole;
  originalRole: UserRole;
  description?: string;
  isNew: boolean;
  isLocked: boolean;
  isOktaSynced: boolean;
}

/* ── SVG Icons ───────────────────────────────────────────────────── */

/**
 * THUB-298: hoisted out of AdminPanel's body.
 *
 * Defined inside the component, this produced a NEW component type on every render, so React
 * unmounted and remounted both header icons instead of updating them (react-hooks/static-
 * components). The sort state it used to close over is passed in instead.
 */
function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: 'asc' | 'desc' }) {
  if (sortField !== field) {
    return (
      <svg className="w-3.5 h-3.5 text-neutral-400 dark:text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
      </svg>
    );
  }
  return sortDir === 'asc' ? (
    <svg className="w-3.5 h-3.5 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M8 15l4-4 4 4" />
    </svg>
  ) : (
    <svg className="w-3.5 h-3.5 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M8 9l4 4 4-4" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-3.5 h-3.5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function SyncIcon({ className }: { className?: string }) {
  return (
    <svg className={className || 'w-3.5 h-3.5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.5 2v6h-6" />
      <path d="M2.5 22v-6h6" />
      <path d="M2.5 11.5a10 10 0 0 1 18.4-4.5" />
      <path d="M21.5 12.5a10 10 0 0 1-18.4 4.5" />
    </svg>
  );
}

/* ── Main Component ──────────────────────────────────────────────── */

export function AdminPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const { subscribe, connectionStatus } = useWebSocket();
  const adminTab: AdminTab = location.pathname.startsWith('/admin/policies') ? 'policies' : 'roles';
  const setAdminTab = (tab: AdminTab) => navigate(tab === 'policies' ? '/admin/policies' : '/admin');
  // THUB-298: the rows are a WORKING COPY over the server read, not a copy made by an effect.
  //
  // This table is editable -- a staged role change, an added row, a delete -- so it cannot be a
  // plain query result. It used to be useState filled by `fetchMappings()` in a mount effect,
  // which is the set-state-in-effect the rule flags. Now the query owns the server rows and
  // `edits` holds the operator's uncommitted changes, with `mappings` derived as
  // `edits ?? serverRows`.
  //
  // A useful consequence rather than just lint compliance: while edits are pending, a background
  // refetch (or the role_mapping_changes broadcast below) can no longer wipe them out. Before,
  // fetchMappings() replaced the array wholesale and an operator mid-edit lost their staged role
  // change without warning.
  const mappingsQuery = useQuery({
    queryKey: queryKeys.rbac.roleMappings,
    queryFn: () => rbacApi.getRoleMappings(),
  });

  const serverRows: MappingRow[] = useMemo(
    () =>
      (mappingsQuery.data ?? []).map((m: RoleMappingResponse) => ({
        groupName: m.groupName,
        role: (m.role || 'viewer') as UserRole,
        originalRole: (m.role || 'viewer') as UserRole,
        description: m.description,
        isNew: false,
        isLocked: m.locked || m.groupName === BREAK_GLASS_GROUP,
        isOktaSynced: (m.cognitoDescription || m.description || '') === OKTA_SYNCED_DESCRIPTION,
      })),
    [mappingsQuery.data]
  );

  const [edits, setEdits] = useState<MappingRow[] | null>(null);
  const mappings = edits ?? serverRows;

  /** Same updater signature the five existing call sites already use. */
  const setMappings = useCallback(
    (updater: MappingRow[] | ((prev: MappingRow[]) => MappingRow[])) => {
      setEdits((prev) => {
        const base = prev ?? serverRows;
        return typeof updater === 'function' ? updater(base) : updater;
      });
    },
    [serverRows]
  );

  const loading = mappingsQuery.isPending;
  const [saving, setSaving] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupError, setNewGroupError] = useState('');
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState<SortField>('groupName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  /** Re-read from the server and discard the working copy, so the fresh rows are what shows. */
  const fetchMappings = useCallback(async () => {
    setEdits(null);
    const result = await mappingsQuery.refetch();
    if (result.error) {
      toast.error((result.error as Error).message || 'Failed to load role mappings');
    }
  }, [mappingsQuery]);

  // Live-refresh role mappings when another user makes changes
  useEffect(() => {
    if (connectionStatus !== 'connected') return;
    const unsub = subscribe('role_mapping_changes', () => {
      fetchMappings();
    });
    return unsub;
  }, [connectionStatus, subscribe, fetchMappings]);

  const filteredAndSorted = useMemo(() => {
    let result = mappings;
    if (filterText.trim()) {
      const lower = filterText.toLowerCase();
      result = result.filter((m) => m.groupName.toLowerCase().includes(lower));
    }
    result = [...result].sort((a, b) => {
      let cmp: number;
      if (sortField === 'groupName') {
        cmp = a.groupName.localeCompare(b.groupName);
      } else {
        cmp = ALL_ROLES.indexOf(a.role) - ALL_ROLES.indexOf(b.role);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [mappings, filterText, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleRoleChange = (groupName: string, newRole: UserRole) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.groupName === groupName ? { ...m, role: newRole } : m
      )
    );
  };

  const handleSave = async (row: MappingRow) => {
    setSaving(row.groupName);
    try {
      await rbacApi.updateRoleMapping(row.groupName, row.role);
      setMappings((prev) =>
        prev.map((m) =>
          m.groupName === row.groupName
            ? { ...m, originalRole: m.role, isNew: false }
            : m
        )
      );
      toast.success(`Updated ${row.groupName} to ${ROLE_LABELS[row.role]}`);
    } catch (err: any) {
      toast.error(err.message || `Failed to update ${row.groupName}`);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (groupName: string) => {
    setDeleting(groupName);
    try {
      await rbacApi.deleteRoleMapping(groupName);
      setMappings((prev) => prev.filter((m) => m.groupName !== groupName));
      toast.success(`Deleted mapping for ${groupName}`);
    } catch (err: any) {
      toast.error(err.message || `Failed to delete ${groupName}`);
    } finally {
      setDeleting(null);
    }
  };

  const validateGroupName = (name: string) => {
    if (!name.trim()) {
      setNewGroupError('');
      return;
    }
    if (!name.startsWith('Team-') && !name.startsWith('Role-')) {
      setNewGroupError('Group name must start with "Team-" or "Role-"');
    } else {
      setNewGroupError('');
    }
  };

  const handleAddMapping = () => {
    const trimmed = newGroupName.trim();
    if (!trimmed) return;
    if (!trimmed.startsWith('Team-') && !trimmed.startsWith('Role-')) {
      setNewGroupError('Group name must start with "Team-" or "Role-"');
      return;
    }
    if (mappings.some((m) => m.groupName === trimmed)) {
      toast.error('Group already has a mapping');
      return;
    }
    setMappings((prev) => [
      ...prev,
      {
        groupName: trimmed,
        role: 'viewer',
        originalRole: 'viewer',
        isNew: true,
        isLocked: false,
        isOktaSynced: false,
      },
    ]);
    setNewGroupName('');
    setNewGroupError('');
  };

  const hasChanges = (row: MappingRow) =>
    row.role !== row.originalRole || row.isNew;

  const canDelete = (row: MappingRow) =>
    !row.isLocked && !row.isOktaSynced;

  return (
    <div className="space-y-8">
      {/* ── Page Header ─────────────────────────────────────────── */}
      <div>
        <h1 className="font-display text-2xl font-bold text-neutral-900 dark:text-white">
          Administration
        </h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-slate-400">
          Manage role mappings and authorization policies for TransferHub.
        </p>
      </div>

      {/* ── Top-level Tab Bar ───────────────────────────────────── */}
      <div className="border-b border-neutral-200 dark:border-slate-700">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setAdminTab('roles')}
            className={`
              pb-3 text-sm font-medium border-b-2 transition-colors
              ${adminTab === 'roles'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-neutral-500 dark:text-slate-400 hover:text-neutral-700 dark:hover:text-slate-300 hover:border-neutral-300 dark:hover:border-slate-600'
              }
            `}
          >
            Role Mappings
          </button>
          <button
            onClick={() => setAdminTab('policies')}
            className={`
              pb-3 text-sm font-medium border-b-2 transition-colors
              ${adminTab === 'policies'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-neutral-500 dark:text-slate-400 hover:text-neutral-700 dark:hover:text-slate-300 hover:border-neutral-300 dark:hover:border-slate-600'
              }
            `}
          >
            Policies
          </button>
        </nav>
      </div>

      {/* ── Tab Content ─────────────────────────────────────────── */}
      {/* Both tabs stay mounted to preserve state (chat history, etc.) */}
      <div className={adminTab === 'policies' ? '' : 'hidden'}>
        <PolicyManagement />
      </div>
      <div className={adminTab === 'roles' ? '' : 'hidden'}>
      <>
      {/* ── Role Mappings Content ───────────────────────────────── */}

      {/* ── Permission Matrix (always visible) ──────────────────── */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-200 dark:border-slate-700">
          <h2 className="font-display font-semibold text-lg text-neutral-900 dark:text-white">
            Role Permissions
          </h2>
          <p className="mt-0.5 text-sm text-neutral-500 dark:text-slate-400">
            Each role inherits all permissions from roles below it.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-slate-700">
                <th className="px-5 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider w-1/2">
                  Permission
                </th>
                {ALL_ROLES.map((role) => (
                  <th key={role} className="px-3 py-3 text-center w-[12.5%]">
                    <div className="flex flex-col items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full ${ROLE_COLORS[role].dot}`} />
                      <span className="text-xs font-semibold text-neutral-700 dark:text-slate-300 whitespace-nowrap">
                        {ROLE_LABELS[role]}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMISSION_MATRIX.map((group, gi) => (
                <React.Fragment key={group.category}>
                  {/* Category header row */}
                  <tr className="bg-neutral-50/70 dark:bg-slate-800/80">
                    <td
                      colSpan={1 + ALL_ROLES.length}
                      className="px-5 py-2 text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider"
                    >
                      {group.category}
                    </td>
                  </tr>
                  {group.permissions.map((perm, pi) => (
                    <tr
                      key={`${gi}-${pi}`}
                      className="border-b border-neutral-100 dark:border-slate-700/50 hover:bg-neutral-50/50 dark:hover:bg-slate-700/20 transition-colors"
                    >
                      <td className="px-5 py-2.5 text-neutral-700 dark:text-slate-300">
                        {perm.label}
                      </td>
                      {ALL_ROLES.map((role) => (
                        <td key={role} className="px-3 py-2.5 text-center">
                          {perm.roles.includes(role) ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-success-100 dark:bg-success-900/30">
                              <CheckIcon className="w-3.5 h-3.5 text-success-600 dark:text-success-400" />
                            </span>
                          ) : (
                            <span className="inline-flex items-center justify-center w-6 h-6 text-neutral-300 dark:text-slate-600">
                              &mdash;
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add New Mapping ─────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-200 dark:border-slate-700">
          <h2 className="font-display font-semibold text-lg text-neutral-900 dark:text-white">
            Add Mapping
          </h2>
          <p className="mt-0.5 text-sm text-neutral-500 dark:text-slate-400">
            Pre-configure a role for an Okta group before it is JIT-provisioned.
          </p>
        </div>
        <div className="px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="flex-1 max-w-md">
              <label className="block text-xs font-medium text-neutral-600 dark:text-slate-400 mb-1.5">
                Okta Role/Team Group Name
              </label>
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => {
                  setNewGroupName(e.target.value);
                  validateGroupName(e.target.value);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleAddMapping()}
                placeholder="e.g., Team-Finance or Role-Admin"
                className={`
                  w-full px-4 py-2.5 rounded-lg text-sm
                  bg-white dark:bg-slate-900
                  border ${newGroupError ? 'border-danger-400 dark:border-danger-500' : 'border-neutral-300 dark:border-slate-600'}
                  text-neutral-900 dark:text-slate-100
                  placeholder:text-neutral-400 dark:placeholder:text-slate-500
                  focus:outline-none focus:ring-2 ${newGroupError ? 'focus:ring-danger-500' : 'focus:ring-primary-500'} focus:border-transparent
                  transition-all duration-200
                `}
              />
              {newGroupError && (
                <p className="mt-1.5 text-xs text-danger-600 dark:text-danger-400">
                  {newGroupError}
                </p>
              )}
            </div>
            <div className="pt-[26px]">
              <button
                onClick={handleAddMapping}
                disabled={!newGroupName.trim() || !!newGroupError}
                className="
                  inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg
                  bg-primary-600 hover:bg-primary-700 text-white
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-150
                "
              >
                <PlusIcon className="w-4 h-4" />
                Add Mapping
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Group-to-Role Mappings Table ─────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-200 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-display font-semibold text-lg text-neutral-900 dark:text-white">
              Group Mappings
            </h2>
            <p className="mt-0.5 text-sm text-neutral-500 dark:text-slate-400">
              {mappings.length} mapping{mappings.length !== 1 ? 's' : ''} configured
            </p>
          </div>
          {!loading && mappings.length > 0 && (
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-slate-500 pointer-events-none" />
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Filter groups..."
                className="
                  w-full sm:w-64 pl-9 pr-3 py-2 rounded-lg text-sm
                  bg-white dark:bg-slate-900
                  border border-neutral-300 dark:border-slate-600
                  text-neutral-900 dark:text-slate-100
                  placeholder:text-neutral-400 dark:placeholder:text-slate-500
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  transition-all duration-200
                "
              />
            </div>
          )}
        </div>

        {loading ? (
          <div className="px-5 py-16 text-center">
            <div className="inline-block w-8 h-8 border-4 border-neutral-200 dark:border-slate-700 border-t-primary-500 rounded-full animate-spin mb-3" />
            <p className="text-sm text-neutral-500 dark:text-slate-400">Loading role mappings...</p>
          </div>
        ) : mappings.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <p className="text-neutral-400 dark:text-slate-500 text-sm">No role mappings configured.</p>
            <p className="text-neutral-400 dark:text-slate-500 text-xs mt-1">Use the form above to add an Okta role or team mapping.</p>
          </div>
        ) : filteredAndSorted.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <p className="text-neutral-400 dark:text-slate-500 text-sm">
              No groups match &ldquo;{filterText}&rdquo;
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 dark:bg-slate-800/50 border-b-2 border-neutral-200 dark:border-slate-700">
                  <th
                    className="px-5 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-neutral-700 dark:hover:text-slate-200 transition-colors"
                    onClick={() => handleSort('groupName')}
                  >
                    <div className="flex items-center gap-1.5">
                      Okta Role/Team
                      <SortIcon field="groupName" sortField={sortField} sortDir={sortDir} />
                    </div>
                  </th>
                  <th
                    className="px-5 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-neutral-700 dark:hover:text-slate-200 transition-colors"
                    onClick={() => handleSort('role')}
                  >
                    <div className="flex items-center gap-1.5">
                      Role
                      <SortIcon field="role" sortField={sortField} sortDir={sortDir} />
                    </div>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-slate-700/50">
                {filteredAndSorted.map((row) => (
                  <tr
                    key={row.groupName}
                    className={`
                      transition-colors duration-150
                      ${row.isLocked
                        ? 'bg-amber-50/40 dark:bg-amber-900/5 hover:bg-amber-50/70 dark:hover:bg-amber-900/10'
                        : hasChanges(row)
                          ? 'bg-primary-50/40 dark:bg-primary-900/5 hover:bg-primary-50/70 dark:hover:bg-primary-900/10'
                          : 'hover:bg-neutral-50 dark:hover:bg-slate-700/30'
                      }
                    `}
                  >
                    {/* Group name */}
                    <td className="px-5 py-3.5">
                      <span className="font-medium text-neutral-900 dark:text-slate-100">
                        {row.groupName}
                      </span>
                    </td>

                    {/* Role selector or locked display */}
                    <td className="px-5 py-3.5">
                      {row.isLocked ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${ROLE_COLORS[row.role].dot}`} />
                          <span className="font-medium text-neutral-900 dark:text-slate-100">
                            {ROLE_LABELS[row.role] || 'System Admin'}
                          </span>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ROLE_COLORS[row.role].dot}`} />
                          <select
                            value={row.role}
                            onChange={(e) => handleRoleChange(row.groupName, e.target.value as UserRole)}
                            className="
                              px-3 py-1.5 rounded-lg text-sm font-medium
                              bg-white dark:bg-slate-900
                              border border-neutral-300 dark:border-slate-600
                              text-neutral-900 dark:text-slate-100
                              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                              cursor-pointer transition-colors
                            "
                          >
                            {ALL_ROLES.map((r) => (
                              <option key={r} value={r}>
                                {ROLE_LABELS[r]}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </td>

                    {/* Status badges */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        {row.isLocked && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                            <LockIcon className="w-3 h-3" />
                            Protected
                          </span>
                        )}
                        {row.isOktaSynced && !row.isLocked && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            <SyncIcon className="w-3 h-3" />
                            Okta Synced
                          </span>
                        )}
                        {row.isNew && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300">
                            New
                          </span>
                        )}
                        {hasChanges(row) && !row.isNew && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                            Unsaved
                          </span>
                        )}
                        {!row.isLocked && !row.isOktaSynced && !row.isNew && !hasChanges(row) && (
                          <span className="text-xs text-neutral-400 dark:text-slate-500">&mdash;</span>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        {!row.isLocked && hasChanges(row) && (
                          <button
                            onClick={() => handleSave(row)}
                            disabled={saving === row.groupName}
                            className="
                              px-3.5 py-1.5 text-xs font-medium rounded-lg
                              bg-primary-600 hover:bg-primary-700 text-white
                              disabled:opacity-50 disabled:cursor-not-allowed
                              transition-colors duration-150
                            "
                          >
                            {saving === row.groupName ? 'Saving...' : 'Save'}
                          </button>
                        )}
                        {canDelete(row) && (
                          <button
                            onClick={() => handleDelete(row.groupName)}
                            disabled={deleting === row.groupName}
                            className="
                              px-3.5 py-1.5 text-xs font-medium rounded-lg
                              text-danger-600 dark:text-danger-400
                              hover:bg-danger-50 dark:hover:bg-danger-900/20
                              border border-danger-200 dark:border-danger-800
                              disabled:opacity-50 disabled:cursor-not-allowed
                              transition-colors duration-150
                            "
                          >
                            {deleting === row.groupName ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Table footer with count */}
        {!loading && filteredAndSorted.length > 0 && (
          <div className="px-5 py-3 bg-neutral-50 dark:bg-slate-800/50 border-t border-neutral-200 dark:border-slate-700 text-xs text-neutral-500 dark:text-slate-400">
            Showing {filteredAndSorted.length} of {mappings.length} mapping{mappings.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </>
      </div>
    </div>
  );
}

export default AdminPanel;
